import React from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import {
  Box,
  Divider,
  DropdownSectionHeader,
  ListItem,
  ListItemProps,
} from 'app/shared/components/atoms';
import { ActionButton } from 'app/shared/components/molecules/ActionButton';

import { OptionsContainer } from './Container';
import { GroupedOptions } from './Grouped';
import MultiSelectOptions from './MultiSelect';
import { SimpleOptions } from './Simple';

export interface SharedOptionsProps<T> {
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string | number;
  invertColors?: boolean;
  zIndex?: number;
}

export interface NoOptionsOption {
  label: string;
  onClick?: () => void;
}

export interface SharedSelectProps<T> extends SharedOptionsProps<T> {
  isLoading?: boolean;
  hasError?: boolean;
  noOptionsMessage?: string;
  groupBy?: (option: T) => string;
  recommendedOptions?: T[];
  getRecommendedOptionsTitle?: (options: T[]) => string;
  mostVisitedOptions?: T[];
  getMostVisitedOptionsTitle?: (options: T[]) => string;
  isSearchable?: boolean;
  userIsSearching?: boolean;
  noOptionsOption?: NoOptionsOption;
  'data-qaid'?: string;
}

interface Props<T>
  extends SharedSelectProps<T>,
    React.ComponentPropsWithoutRef<any> {
  isOpen: boolean;
  selectRef: React.RefObject<any>;
  optionsRef: React.RefObject<any>;
  onOptionClick: (option: T) => void;
  isMultiSelect?: boolean;
  multiSelectValues: (string | number)[];
  onResetMultiSelect: () => void;
  unfilteredOptions: T[];
  moreOptionsTitle?: string;
  multiSelectSelectedTitle?: string;
}

const defaultNoOptionsOption: NoOptionsOption = {
  label: 'No options...',
  onClick: undefined,
};

export const Option = (props: React.PropsWithChildren<ListItemProps>) => (
  <ListItem fontSize="fontSize100" lineHeight="100%" px={4} py={3} {...props} />
);

export const Options = <T = any,>({
  isOpen,
  isLoading = false,
  hasError = false,
  options,
  unfilteredOptions,
  noOptionsMessage,
  onOptionClick,
  getOptionLabel,
  getOptionValue,
  getRecommendedOptionsTitle = (recommendedOptions) =>
    `Recommended ${recommendedOptions.length}`,
  getMostVisitedOptionsTitle = (mostVisitedOptions) =>
    `Top ${mostVisitedOptions.length}`,
  recommendedOptions,
  mostVisitedOptions,
  groupBy,
  isSearchable,
  userIsSearching,
  'data-qaid': qaId,
  noOptionsOption = defaultNoOptionsOption,
  invertColors,
  selectRef,
  optionsRef,
  multiSelectValues,
  onResetMultiSelect,
  moreOptionsTitle,
  multiSelectSelectedTitle,
  isMultiSelect = false,
  zIndex = 1007,
}: Props<T>) => {
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();

  const showRecommendedOptions =
    recommendedOptions !== undefined &&
    recommendedOptions.length > 0 &&
    !userIsSearching;
  const showMostVisitedOptions =
    mostVisitedOptions !== undefined &&
    mostVisitedOptions.length > 0 &&
    !userIsSearching;

  if (!isOpen) {
    return null;
  }

  const selectedOption = selectRef.current?.value;

  const backgroundColor = invertColors
    ? colors.primary.black.base
    : colors.primary.white.lightest;
  const dividerColor = invertColors
    ? colors.primary.black.light
    : colors.accent.primaryAccent.base;

  function renderContent() {
    if (hasError) {
      return (
        <Option isDisabled isLastChild invertColors={invertColors}>
          {formatMessage({ id: 'typeahead.errorPleaseTryAgain' })}
        </Option>
      );
    }

    if (isLoading) {
      return (
        <Option isDisabled isLastChild invertColors={invertColors}>
          {formatMessage({ id: 'typeahead.loadingOptions' })}
        </Option>
      );
    }

    if (
      (showRecommendedOptions || showMostVisitedOptions) &&
      !userIsSearching
    ) {
      return (
        <Box overflowY="scroll" maxHeight="inherit">
          {showRecommendedOptions && (
            <Box as="ul" p={0} m={0}>
              <DropdownSectionHeader
                data-qaid={`${qaId}-recommended-header`}
                invertColors={invertColors}
              >
                {getRecommendedOptionsTitle(recommendedOptions)}
              </DropdownSectionHeader>
              <SimpleOptions<T>
                options={recommendedOptions}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                onOptionClick={onOptionClick}
                invertColors={invertColors}
                selectedOption={selectedOption}
              />
            </Box>
          )}
          {showRecommendedOptions && showMostVisitedOptions && (
            <Divider mt={0} mb={0} color={dividerColor} />
          )}
          {showMostVisitedOptions && (
            <Box as="ul" p={0} m={0}>
              <DropdownSectionHeader
                data-qaid={`${qaId}-popular-header`}
                invertColors={invertColors}
              >
                {getMostVisitedOptionsTitle(mostVisitedOptions)}
              </DropdownSectionHeader>
              <SimpleOptions<T>
                options={mostVisitedOptions}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                onOptionClick={onOptionClick}
                invertColors={invertColors}
                selectedOption={selectedOption}
              />
            </Box>
          )}
        </Box>
      );
    }

    if (
      (!options || options.length === 0) &&
      isSearchable &&
      userIsSearching &&
      !isMultiSelect
    ) {
      const onNoOptionsClick = noOptionsOption.onClick;
      return (
        <Option
          onClick={onNoOptionsClick}
          isDisabled={!onNoOptionsClick}
          isLastChild
          invertColors={invertColors}
        >
          {noOptionsOption.label}
        </Option>
      );
    }

    if (!isMultiSelect && isSearchable && (!options || options.length === 0)) {
      return (
        <Option isDisabled isLastChild invertColors={invertColors}>
          {noOptionsMessage
            ? noOptionsMessage
            : formatMessage({ id: 'typeahead.noResults' })}
        </Option>
      );
    }

    if (isMultiSelect) {
      const selectedOptions = unfilteredOptions.filter((opt: T) =>
        multiSelectValues.includes(getOptionValue(opt))
      );
      const selectedOptionsLength = selectedOptions?.length || 0;
      const unselectedOptions = options.filter(
        (opt: T) => !multiSelectValues.includes(getOptionValue(opt))
      );
      const unselectedOptionsLength = unselectedOptions?.length || 0;

      return (
        <Box overflowY="scroll" maxHeight="inherit">
          {userIsSearching && unselectedOptionsLength > 0 && (
            <Box as="ul" p={0} m={0}>
              <MultiSelectOptions<T>
                onOptionClick={onOptionClick}
                getOptionLabel={getOptionLabel}
                options={unselectedOptions}
                getOptionValue={getOptionValue}
                invertColors={invertColors}
                selectedOptions={multiSelectValues}
                displayBottomBorderOnLastChild={true}
              />
            </Box>
          )}
          {userIsSearching && unselectedOptionsLength === 0 && (
            <Box as="ul" p={0} m={0}>
              <Option isLastChild invertColors={invertColors}>
                {noOptionsOption.label}
              </Option>
            </Box>
          )}
          {selectedOptionsLength > 0 && (
            <Box as="ul" p={0} m={0}>
              <DropdownSectionHeader
                data-qaid={`${qaId}-multiselect-applied-header`}
                invertColors={invertColors}
              >
                {multiSelectSelectedTitle ||
                  formatMessage({ id: 'typeahead.multiSelectSelectedTitle' })}
              </DropdownSectionHeader>
              <MultiSelectOptions<T>
                onOptionClick={onOptionClick}
                getOptionLabel={getOptionLabel}
                options={selectedOptions}
                getOptionValue={getOptionValue}
                invertColors={invertColors}
                selectedOptions={multiSelectValues}
              />
            </Box>
          )}
          {!userIsSearching && (
            <Box as="ul" p={0} m={0}>
              {selectedOptionsLength > 0 && (
                <DropdownSectionHeader
                  data-qaid={`${qaId}-multiselect-more-options-header`}
                  invertColors={invertColors}
                >
                  {moreOptionsTitle ||
                    formatMessage({
                      id: 'typeahead.multiSelectMoreOptionsTitle',
                    })}
                </DropdownSectionHeader>
              )}
              <MultiSelectOptions<T>
                onOptionClick={onOptionClick}
                getOptionLabel={getOptionLabel}
                options={unselectedOptions}
                getOptionValue={getOptionValue}
                invertColors={invertColors}
                selectedOptions={multiSelectValues}
              />
            </Box>
          )}
          {selectedOptionsLength > 0 && (
            <Box
              zIndex={zIndex + 1}
              position="sticky"
              bottom="0px"
              left="0px"
              w="100%"
              px={3}
              py={4}
              bt={dividerColor}
              backgroundColor={backgroundColor}
            >
              <ActionButton
                variant={invertColors ? 'tertiary' : 'secondary'}
                fullWidth
                onClick={onResetMultiSelect}
                data-qaid={`${qaId}-multiselect-reset`}
              >
                {formatMessage({ id: 'reset' })}
              </ActionButton>
            </Box>
          )}
        </Box>
      );
    }

    if (groupBy) {
      return (
        <GroupedOptions<T>
          options={options}
          onOptionClick={onOptionClick}
          groupBy={groupBy}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          invertColors={invertColors}
          selectedOption={selectedOption}
          data-qaid={qaId}
        />
      );
    }

    return (
      <Box as="ul" p={0} m={0} overflowY="scroll" maxHeight="inherit">
        <SimpleOptions
          onOptionClick={onOptionClick}
          getOptionLabel={getOptionLabel}
          options={options}
          getOptionValue={getOptionValue}
          invertColors={invertColors}
          selectedOption={selectedOption}
        />
      </Box>
    );
  }

  return (
    <OptionsContainer
      isSearchable={isSearchable}
      selectRef={selectRef}
      optionsLength={options.length}
      backgroundColor={backgroundColor}
      optionsRef={optionsRef}
      zIndex={zIndex}
    >
      {renderContent()}
    </OptionsContainer>
  );
};
